import axios from 'axios';

const RAW_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const API_BASE = RAW_API_URL.endsWith('/api') ? RAW_API_URL : `${RAW_API_URL}/api`;

const getToken = () => localStorage.getItem('token');

const authHeaders = (isMultipart = false) => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (isMultipart) {
    headers['Content-Type'] = 'multipart/form-data';
  }
  return { headers };
};

/**
 * Handle API error responses gracefully.
 */
const handleApiError = (error, defaultMsg = 'Une erreur est survenue') => {
  const message = error.response?.data?.message || error.response?.data?.error || error.message || defaultMsg;
  const status = error.response?.status;
  const data = error.response?.data;
  const customError = new Error(message);
  customError.status = status;
  customError.data = data;
  throw customError;
};

// ============================================================================
// 1. CATÉGORIES D'ACTUALITÉS (/api/news/categories)
// ============================================================================

export const getCategories = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE}/news/categories`, {
      params,
      ...authHeaders(),
    });
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer les catégories');
  }
};

export const getCategoryById = async (id) => {
  try {
    const res = await axios.get(`${API_BASE}/news/categories/${id}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer la catégorie');
  }
};

export const createCategory = async (payload) => {
  try {
    const res = await axios.post(`${API_BASE}/news/categories`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de créer la catégorie');
  }
};

export const updateCategory = async (id, payload) => {
  try {
    const res = await axios.put(`${API_BASE}/news/categories/${id}`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de modifier la catégorie');
  }
};

export const deleteCategory = async (id) => {
  try {
    const res = await axios.delete(`${API_BASE}/news/categories/${id}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de supprimer la catégorie');
  }
};

// ============================================================================
// 2. ARTICLES D'ACTUALITÉS (/api/news/articles)
// ============================================================================

export const getArticles = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE}/news/articles`, {
      params,
      ...authHeaders(),
    });
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer les articles');
  }
};

export const getArticleById = async (id) => {
  try {
    const res = await axios.get(`${API_BASE}/news/articles/${id}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de charger l'article");
  }
};

export const createArticle = async (payload) => {
  try {
    const res = await axios.post(`${API_BASE}/news/articles`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de créer l'article");
  }
};

export const updateArticle = async (id, payload) => {
  try {
    const res = await axios.put(`${API_BASE}/news/articles/${id}`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de modifier l'article");
  }
};

export const deleteArticle = async (id) => {
  try {
    const res = await axios.delete(`${API_BASE}/news/articles/${id}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de supprimer l'article");
  }
};

export const restoreArticle = async (id) => {
  try {
    const res = await axios.put(`${API_BASE}/news/articles/${id}/restore`, {}, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de restaurer l'article");
  }
};

export const approveArticle = async (id) => {
  try {
    const res = await axios.put(`${API_BASE}/news/articles/${id}/approve`, {}, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible d'approuver l'article");
  }
};

export const rejectArticle = async (id, rejectionReason) => {
  try {
    const res = await axios.put(
      `${API_BASE}/news/articles/${id}/reject`,
      { rejectionReason },
      authHeaders()
    );
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de rejeter l'article");
  }
};

export const publishArticle = async (id) => {
  try {
    const res = await axios.put(`${API_BASE}/news/articles/${id}/publish`, {}, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de publier l'article");
  }
};

export const unpublishArticle = async (id) => {
  try {
    const res = await axios.put(`${API_BASE}/news/articles/${id}/unpublish`, {}, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de dépublier l'article");
  }
};

export const archiveArticle = async (id) => {
  try {
    const res = await axios.put(`${API_BASE}/news/articles/${id}/archive`, {}, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible d'archiver l'article");
  }
};

export const getArticleAuditLogs = async (id) => {
  try {
    const res = await axios.get(`${API_BASE}/news/articles/${id}/audit-logs`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de charger l'historique d'audit");
  }
};

export const uploadNewsImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image', file);
    const res = await axios.post(
      `${API_BASE}/news/media/upload`,
      formData,
      authHeaders(true)
    );
    return res.data;
  } catch (err) {
    handleApiError(err, "Échec de l'upload de l'image");
  }
};

// ============================================================================
// 3. ABONNÉS NEWSLETTER (/api/newsletter/subscribers)
// ============================================================================

export const getSubscribers = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE}/newsletter/subscribers`, {
      params,
      ...authHeaders(),
    });
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de charger la liste des abonnés');
  }
};

export const getSubscriberById = async (id) => {
  try {
    const res = await axios.get(`${API_BASE}/newsletter/subscribers/${id}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de charger les détails de l'abonné");
  }
};

export const createSubscriber = async (payload) => {
  try {
    const res = await axios.post(`${API_BASE}/newsletter/subscribers`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible d'ajouter l'abonné");
  }
};

export const updateSubscriber = async (id, payload) => {
  try {
    const res = await axios.put(`${API_BASE}/newsletter/subscribers/${id}`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de modifier l'abonné");
  }
};

export const excludeSubscriber = async (id, reason) => {
  try {
    const res = await axios.put(
      `${API_BASE}/newsletter/subscribers/${id}/exclude`,
      { reason },
      authHeaders()
    );
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible d'exclure l'abonné");
  }
};

export const includeSubscriber = async (id) => {
  try {
    const res = await axios.put(`${API_BASE}/newsletter/subscribers/${id}/include`, {}, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de réintégrer l'abonné");
  }
};

export const deleteSubscriber = async (id) => {
  try {
    const res = await axios.delete(`${API_BASE}/newsletter/subscribers/${id}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de supprimer l'abonné");
  }
};

// ============================================================================
// 4. CAMPAGNES & LIVRABILITÉ NEWSLETTER (/api/newsletter/campaigns)
// ============================================================================

export const getCampaigns = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE}/newsletter/campaigns`, {
      params,
      ...authHeaders(),
    });
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de charger les campagnes');
  }
};

export const getCampaignById = async (id) => {
  try {
    const res = await axios.get(`${API_BASE}/newsletter/campaigns/${id}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de charger la campagne');
  }
};

export const createCampaign = async (payload) => {
  try {
    const res = await axios.post(`${API_BASE}/newsletter/campaigns`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de créer la campagne');
  }
};

export const updateCampaign = async (id, payload) => {
  try {
    const res = await axios.put(`${API_BASE}/newsletter/campaigns/${id}`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de mettre à jour la campagne');
  }
};

export const deleteCampaign = async (id) => {
  try {
    const res = await axios.delete(`${API_BASE}/newsletter/campaigns/${id}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de supprimer la campagne');
  }
};

export const updateCampaignRecipients = async (id, payload) => {
  try {
    const res = await axios.put(
      `${API_BASE}/newsletter/campaigns/${id}/recipients`,
      payload,
      authHeaders()
    );
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de mettre à jour les destinataires');
  }
};

export const getCampaignPreview = async (id) => {
  try {
    const res = await axios.get(`${API_BASE}/newsletter/campaigns/${id}/preview`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de générer la prévisualisation');
  }
};

export const sendTestBat = async (id, testEmails) => {
  try {
    const res = await axios.post(
      `${API_BASE}/newsletter/campaigns/${id}/test`,
      { testEmails },
      authHeaders()
    );
    return res.data;
  } catch (err) {
    handleApiError(err, "Échec de l'envoi de l'email de test (BAT)");
  }
};

export const sendCampaign = async (id, idempotencyKey, confirmationText = 'ENVOYER') => {
  try {
    const res = await axios.post(
      `${API_BASE}/newsletter/campaigns/${id}/send`,
      {
        confirmSend: true,
        confirmationText,
        idempotencyKey,
      },
      {
        ...authHeaders(),
        headers: {
          ...authHeaders().headers,
          'Idempotency-Key': idempotencyKey,
        },
      }
    );
    return res.data;
  } catch (err) {
    handleApiError(err, "Échec du déclenchement de l'envoi de la campagne");
  }
};

export const getCampaignDeliveries = async (id, params = {}) => {
  try {
    const res = await axios.get(`${API_BASE}/newsletter/campaigns/${id}/deliveries`, {
      params,
      ...authHeaders(),
    });
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer le journal des livraisons');
  }
};

export const reconcileDeliveries = async (campaignId, resolutions) => {
  try {
    const res = await axios.post(
      `${API_BASE}/newsletter/campaigns/${campaignId}/reconcile-deliveries`,
      { resolutions },
      authHeaders()
    );
    return res.data;
  } catch (err) {
    handleApiError(err, 'Échec de la réconciliation administrative');
  }
};

export const retryFailedDeliveries = async (campaignId) => {
  try {
    const res = await axios.post(
      `${API_BASE}/newsletter/campaigns/${campaignId}/retry-failed`,
      {},
      authHeaders()
    );
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de relancer les livraisons en échec');
  }
};

export const getNewsletterStats = async () => {
  try {
    const res = await axios.get(`${API_BASE}/newsletter/stats`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer les statistiques de newsletter');
  }
};

// ============================================================================
// 6. ADMINISTRATION ET CONTRÔLE DE L'AGENT IA (/api/news/agent-admin)
// ============================================================================

export const getNewsAgentStatus = async () => {
  try {
    const res = await axios.get(`${API_BASE}/news/agent-admin/status`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de récupérer l'état du News Agent");
  }
};

export const triggerNewsAgent = async (payload = { mode: 'DRY_RUN', limit: 5 }) => {
  try {
    const res = await axios.post(`${API_BASE}/news/agent-admin/trigger`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Échec du déclenchement de la collecte du News Agent");
  }
};

export const getNewsAgentHistory = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE}/news/agent-admin/history`, {
      params,
      ...authHeaders(),
    });
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de récupérer l'historique des collectes");
  }
};

export const getNewsAgentRunDetail = async (runId) => {
  try {
    const res = await axios.get(`${API_BASE}/news/agent-admin/history/${runId}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de récupérer le détail de la collecte");
  }
};

export const toggleNewsAgentSchedule = async (enabled) => {
  try {
    const res = await axios.post(
      `${API_BASE}/news/agent-admin/toggle-schedule`,
      { enabled },
      authHeaders()
    );
    return res.data;
  } catch (err) {
    handleApiError(err, "Impossible de modifier la planification automatique");
  }
};

